import type { PublicPlan as PublicPlanV2 } from '@wix/ambassador-pricing-plans-v2-plan/types';
import type { Plan as PlanV3 } from '@wix/ambassador-pricing-plans-v3-plan/types';
import type { GetTranslatedPricingDetailsOptions, TranslatedPricingDetails } from '@wix/pricing-plans-tpa-api';
import { getPlanPrice, getVariantPrice, isPlanV3 } from '@wix/pricing-plans-utils';
import type { CreateCurrencyFormatter, TFunction } from '@wix/yoshi-flow-editor';
import { getFormattedPriceData } from '../hooks';
import { getFormattedAdditionalFees } from '../utils/get-formatted-additional-fees';
import { getFreeTrialDaysLabel } from '../utils/getFreeTrialDaysLabel';
import { getPeriodLabel } from '../utils/getPeriodLabel';
import { getPlanDuration } from '../utils/getPlanValidityCycle';
import {
  getVariantAdditionalFees,
  getVariantFreeTrialDaysLabel,
  getVariantPeriodLabel,
  getVariantDuration,
} from '../utils/plan-v3-utils';

interface GetTranslatedPricingDetailsContext {
  t: TFunction;
  locale: string | undefined;
  createCurrencyFormatter: CreateCurrencyFormatter;
}

export function createGetTranslatedPricingDetails(
  context: GetTranslatedPricingDetailsContext,
): (plan: PublicPlanV2 | PlanV3, options?: GetTranslatedPricingDetailsOptions) => TranslatedPricingDetails {
  return (plan, options) =>
    isPlanV3(plan)
      ? getTranslatedPricingDetailsV3(plan, context, options)
      : getTranslatedPricingDetailsV2(plan, context, options);
}

function getTranslatedPricingDetailsV2(
  plan: PublicPlanV2,
  context: GetTranslatedPricingDetailsContext,
  options?: GetTranslatedPricingDetailsOptions,
): TranslatedPricingDetails {
  const { value, currency } = getPlanPrice(plan);
  const { t, locale, createCurrencyFormatter } = context;
  const { trimTrailingZeroes: hideTrailingFractionZeroes } = options ?? {};

  const formattedPriceData = getFormattedPriceData({
    createCurrencyFormatter,
    locale,
    value,
    currency,
    hideTrailingFractionZeroes,
  });

  return {
    price: formattedPriceData.fullPrice,
    cycle: getPeriodLabel(plan.pricing?.subscription, t) ?? '',
    duration: getPlanDuration(plan, t),
    freeTrial: getFreeTrialDaysLabel(plan, t),
    additionalFees: getFormattedAdditionalFees({
      plan,
      locale,
      createCurrencyFormatter,
      hideTrailingFractionZeroes,
    }),
  };
}

function getTranslatedPricingDetailsV3(
  plan: PlanV3,
  context: GetTranslatedPricingDetailsContext,
  options?: GetTranslatedPricingDetailsOptions,
): TranslatedPricingDetails {
  const variant = plan.pricingVariants![0];
  const { value, currency } = getVariantPrice(variant, plan.currency);
  const { t, locale, createCurrencyFormatter } = context;
  const { trimTrailingZeroes: hideTrailingFractionZeroes } = options ?? {};

  const formattedPriceData = getFormattedPriceData({
    createCurrencyFormatter,
    locale,
    value: value?.toString(),
    currency,
    hideTrailingFractionZeroes,
  });

  return {
    price: formattedPriceData.fullPrice,
    cycle: getVariantPeriodLabel(variant, t) ?? '',
    duration: getVariantDuration(variant, t),
    freeTrial: getVariantFreeTrialDaysLabel(variant, t),
    additionalFees: getVariantAdditionalFees({
      variant,
      currency: plan.currency,
      locale,
      createCurrencyFormatter,
      hideTrailingFractionZeroes,
    }),
  };
}
